import { navigate } from 'gatsby';
import { T, always, cond, equals } from 'ramda';
import React, { FC, useEffect, useMemo, useState } from 'react';
import ReactCanvasConfetti from 'react-canvas-confetti';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FolxLoader from '@ui/atoms/FolxLoader';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import SubheadingText from '@ui/atoms/SubheadingText';
import TextLink from '@ui/atoms/TextLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItemComponent from '@ui/molecules/CenteredGridItem/CenteredGridItem.component';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useConfetti from '@hooks/useConfetti';
import useMemberInfo, { IFragment, SubscriptionInfo } from '@hooks/useMemberInfo';
import useWindowSize from '@hooks/useWindowSize';

import { commonLabels } from '@utils/button';
import confetti from '@utils/confetti';
import { doesNotExist, exists } from '@utils/general';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { membership, membershipName } from '@graphql/fragments/membership';

import theme from '@theme/FolxTheme';

const AppointmentErrorBox = styled(FlexColumn)`
	background-color: ${theme.colors.white};
	border-radius: ${theme.radius.lg};
	padding: ${theme.spacing.sm} ${theme.spacing.md};
	border: 1px solid ${theme.colors.red};
	width: 100%;
	gap: ${theme.spacing.sm};
	align-items: center;

	${Button} {
		width: auto;
	}

	${SubheadingText}, ${BasicText} {
		width: 100%;
	}
`;

export interface INextStepsStepPageContent {
	pageHeader: string;
	pageDescription: string;
	altDescription: string;
	getCareLabel: string;
	getCareHref: string;
	exploreLabel: string;
	exploreHref: string;
	appointmentErrorHeader: string;
	appointmentErrorInfo: string;
	rescheduleAppointmentButton: string;
}

interface INextStepsStepProps extends IFlowStepComponentProps {
	pageContent: INextStepsStepPageContent;
	loading?: boolean;
	qualtrics?: { distributionLink: string; surveyStatus: string };
	inMemberProfile?: boolean;
}

const FullWidthColumn = aligned<IFlexColumn>(
	spaced<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn))
);
const FullWidthButton = fullWidth<IButtonProps>(Button);

const NextStepsStep: FC<INextStepsStepProps> = ({
	pageContent,
	loading = false,
	qualtrics,
	existingFormData,
	inMemberProfile = false,
}) => {
	const {
		pageHeader,
		pageDescription,
		altDescription,
		getCareLabel,
		getCareHref,
		exploreLabel,
		exploreHref,
		appointmentErrorHeader,
		appointmentErrorInfo,
		rescheduleAppointmentButton,
	} = pageContent;

	const location = useLocation();

	// Confetti Configuration
	const { windowSize } = useWindowSize();
	const multiplier = cond([
		[equals('mobile'), always(1)],
		[equals('tablet'), always(2)],
		[equals('laptop'), always(3)],
		[equals('desktop'), always(4)],
		[T, always(1)],
	])(windowSize);

	const originY = cond([
		[equals('mobile'), always(0.3)],
		[equals('tablet'), always(0.4)],
		[equals('laptop'), always(0.5)],
		[equals('desktop'), always(0.6)],
	])(windowSize);

	const defaultStartVelocity = 25;
	const confettiProps = { startVelocity: defaultStartVelocity * multiplier };

	const left = confetti(confettiProps).membershipSpread.left;
	const right = confetti(confettiProps).membershipSpread.right;

	left.origin.y = originY;
	right.origin.y = originY;

	const defaultInterval = 40;
	const interval = defaultInterval / multiplier;

	const settings = [left, right];
	const { getInstance, defaultCanvasStyle, startAnimation, pauseAnimation } = useConfetti(
		settings,
		interval
	);

	// If you click the "book a visit" button too fast, you might win the race vs the stripe webhook for successful checkouts, causing our query to mark you as not subscribed and send you back to onboarding
	const [raceConditionLoading, setRaceConditionLoading] = useState(true);

	const fragments: IFragment[] = [{ name: membershipName, node: membership }];
	const { refetchMemberInfo } = useMemberInfo<SubscriptionInfo>(fragments);

	const checkForMembership = async () => {
		// Refetching subscription to check if webhook event has executed
		const { data: memberInfoData } = await refetchMemberInfo();
		if (memberInfoData.authenticatedMember.membershipSubscription) {
			// Subscription found, can safely allow the user to continue
			setRaceConditionLoading(false);
			startAnimation();
			setTimeout(() => {
				pauseAnimation();
			}, 4000);
		} else {
			// Subscription not found yet, retry in 2 seconds
			setTimeout(() => {
				checkForMembership();
			}, 2000);
		}
	};

	useEffect(() => {
		setTimeout(() => {
			checkForMembership();
		}, 2000);
	}, []);

	const newMember = qualtrics?.surveyStatus === 'NEW';

	const getPageLinks = useMemo(() => {
		if (doesNotExist(qualtrics)) {
			return (
				<>
					<FullWidthButton label={getCareLabel} href={getCareHref} variant="secondary" />
					{exploreLabel && <TextLink label={exploreLabel} href={exploreHref} />}
				</>
			);
		}

		return newMember ? (
			<>
				<FullWidthButton
					label={getCareLabel}
					href={qualtrics?.distributionLink}
					variant="secondary"
					newTab
				/>
				<TextLink label={exploreLabel} href={exploreHref} />
			</>
		) : (
			<FullWidthButton label={commonLabels.account} href={exploreHref} variant="secondary" />
		);
	}, [qualtrics]);

	if (
		(!inMemberProfile &&
			existingFormData?.paymentConfirmation &&
			existingFormData?.cogsworthId === null) ||
		location.state?.confirmation === 'FAILED'
	) {
		return (
			<CenteredGridItemComponent>
				<AppointmentErrorBox>
					<SubheadingText>{appointmentErrorHeader}</SubheadingText>
					<BasicText>{appointmentErrorInfo}</BasicText>
					<Button
						label={rescheduleAppointmentButton}
						onClick={() => {
							navigate(
								`/app/member/appointments/reschedule/${
									existingFormData?.paymentConfirmation || location.state?.cogsworthId
								}`
							);
						}}
					/>
				</AppointmentErrorBox>
			</CenteredGridItemComponent>
		);
	}

	return (
		<LoadingWrapper loading={loading || raceConditionLoading} skeleton={<FolxLoader />}>
			<ReactCanvasConfetti refConfetti={getInstance} style={defaultCanvasStyle} />
			<FullWidthColumn alignItems="flex-start" gap="sm" marginTop="xxxxl" marginBottom="xxl">
				<HeadingText fontSize="md" fontWeight="normal">
					{pageHeader}
				</HeadingText>
				<WYSIWYGContent
					dangerouslySetInnerHTML={{
						__html: exists(qualtrics) && !newMember ? altDescription : pageDescription,
					}}
				/>
			</FullWidthColumn>
			<FullWidthColumn alignItems="center" gap="sm" marginBottom="xxxxl">
				{getPageLinks}
			</FullWidthColumn>
		</LoadingWrapper>
	);
};

export default NextStepsStep;
